import * as turf from "@turf/turf";
import type { Polygon } from "geojson";
import { fx_truncarPolygon } from "./AoiPolygonUnion";

/** Polígono candidato a lote, con el nombre que traía (del KML) o `null`. */
export type PoligonoConNombre = {
  polygon: Polygon;
  nombre: string | null;
  /** Solo para piezas del mapa del paso 3: si es nueva o se editó (ver
   *  `AoiDrawToolbar.getPiezas`). La depuración la conserva tal cual. */
  modificada?: boolean;
};

export type ResultadoDepuracion = {
  /** Los que sirven como lote, en el orden en que llegaron. */
  validos: PoligonoConNombre[];
  /** Descartados por error topológico: anillo que se cruza a sí mismo,
   *  menos de 4 puntos o área nula. */
  invalidos: number;
  /** Descartados por ser copia de otro (se solapan en casi toda su área). */
  duplicados: number;
  /** Descartados por solaparse en parte con otro ya aceptado. */
  solapados: number;
};

/** Área mínima, en m², de un polígono para tomarlo como lote. */
const AREA_MINIMA_M2 = 1;

/** Solape tolerado entre dos lotes, en m² — la misma tolerancia que aplica
 *  el servidor (`ToleranciaAreaMetrosCuadrados`). Compartir un borde da un
 *  solape nulo y es válido. */
const SOLAPE_TOLERADO_M2 = 1;

/** Fracción del área a partir de la cual un solape cuenta como DUPLICADO y
 *  no como solape parcial: solo cambia cómo se informa, se descarta igual. */
const FRACCION_DUPLICADO = 0.99;

type Candidato = {
  item: PoligonoConNombre;
  orden: number;
  area: number;
  bbox: [number, number, number, number];
};

/** Deja solo los polígonos que pueden ser lotes: topológicamente válidos,
 *  sin duplicados y sin solaparse entre sí. Cada lote tiene que ser un
 *  pedazo distinto del predio: dos polígonos repetidos terminaban como dos
 *  lotes iguales (y el servidor ahora los rechaza).
 *
 *  Los solapes se resuelven de MENOR a MAYOR área: se conserva el más chico
 *  y se descarta el que lo pisa. Así, si un archivo trae el perímetro de la
 *  finca y además sus lotes, quedan los lotes (el perímetro los pisa a
 *  todos) — y el perímetro global igual se reconstruye con su unión.
 *
 *  Cada polígono descartado por inválido se escribe en la consola con el
 *  motivo, los puntos del anillo, los vértices repetidos seguidos, el primer
 *  y los últimos vértices y dónde están los cruces (diagnóstico del
 *  2026-10-27: un KML cuyo único polígono se descartaba al «Continuar» del
 *  paso 3). El build de producción quita los `console` (`drop` en
 *  `build.js`). */
export function fx_depurarPoligonos(
  items: readonly PoligonoConNombre[],
): ResultadoDepuracion {
  let invalidos = 0;
  let duplicados = 0;
  let solapados = 0;

  // Redondeo a 7 decimales y, enseguida, sellado de bordes: el redondeo
  // saca de la arista a los vértices "en T" y el sellado los vuelve a meter
  // como vértice compartido (ver `sellarBordes`).
  const sellados = sellarBordes(items.map((item) => fx_truncarPolygon(item.polygon)));

  const candidatos: Candidato[] = [];
  items.forEach((item, orden) => {
    const polygon = sellados[orden];
    const anillo = polygon.coordinates[0];
    if (!anillo || anillo.length < 4) {
      console.log(
        "[depurarPoligonos] descartado: anillo con menos de 4 puntos",
        JSON.stringify({ orden, puntos: anillo?.length ?? 0 }),
      );
      invalidos++;
      return;
    }
    try {
      const feature = turf.polygon(polygon.coordinates);
      const area = turf.area(feature);
      const cruces = turf.kinks(feature).features;
      if (cruces.length > 0 || !(area >= AREA_MINIMA_M2)) {
        console.log(
          "[depurarPoligonos] descartado por error topológico",
          JSON.stringify({
            orden,
            puntos: anillo.length,
            repetidosSeguidos: anillo.filter(
              (c, i) => i > 0 && c[0] === anillo[i - 1][0] && c[1] === anillo[i - 1][1],
            ).length,
            primero: anillo[0],
            ultimos: anillo.slice(-3),
            cruces: cruces.slice(0, 5).map((cruce) => cruce.geometry.coordinates),
            areaM2: area,
          }),
        );
        invalidos++;
        return;
      }
      candidatos.push({
        item: { ...item, polygon },
        orden,
        area,
        bbox: turf.bbox(feature) as [number, number, number, number],
      });
    } catch (error) {
      // turf no pudo ni armar el polígono: geometría rota.
      console.log(
        "[depurarPoligonos] descartado: turf no pudo armar el polígono",
        JSON.stringify({ orden, puntos: anillo.length }),
        error,
      );
      invalidos++;
    }
  });

  const aceptados: Candidato[] = [];
  [...candidatos]
    .sort((a, b) => a.area - b.area)
    .forEach((candidato) => {
      const conflicto = conflictoCon(candidato, aceptados);
      if (conflicto === "duplicado") duplicados++;
      else if (conflicto === "solapado") solapados++;
      else aceptados.push(candidato);
    });

  return {
    validos: aceptados
      .sort((a, b) => a.orden - b.orden)
      .map((candidato) => candidato.item),
    invalidos,
    duplicados,
    solapados,
  };
}

/** Distancia máxima, en grados (~10 cm), a la que un vértice de un polígono
 *  se considera SOBRE la arista de otro. Muy por encima del corrimiento que
 *  produce el redondeo a 7 decimales (~0.5 cm) y muy por debajo de cualquier
 *  separación real entre dos lotes. */
const TOLERANCIA_SELLADO_GRADOS = 0.1 / 111320;

/** Hace que polígonos vecinos compartan EXACTAMENTE los vértices de su borde
 *  común: cada vértice de un polígono que cae sobre una arista de otro (sin
 *  ser vértice suyo) se inserta en esa arista.
 *
 *  **Por qué.** Los archivos suelen traer vértices "en T": el vértice de un
 *  lote cae sobre el borde del vecino, que en ese tramo es una recta sin ese
 *  punto. Con las coordenadas originales (14 decimales) encajan perfecto,
 *  pero al redondear a 7 (~1 cm) el vértice se corre hasta medio centímetro
 *  fuera de la recta, y a lo largo de un borde de cientos de metros eso deja
 *  una franja solapada de más de 1 m² entre los dos lotes: se descartaban
 *  como "solapados" aunque visualmente encajan. Caso real medido
 *  (`PREDIO2_TEST.kml`): 6 pares entre 1.07 y 1.70 m²; con el sellado, 0.
 *  Es el mismo arreglo que `LoteSplitState._sellarBordes` aplica al cortar.
 *
 *  Devuelve polígonos nuevos (no toca los de entrada); solo el anillo
 *  exterior, que es el que comparte bordes. */
function sellarBordes(poligonos: readonly Polygon[]): Polygon[] {
  const anillos = poligonos.map((p) =>
    (p.coordinates[0] ?? []).map((c) => [c[0], c[1]]),
  );
  const cajas = anillos.map(cajaDe);

  for (let i = 0; i < anillos.length; i++) {
    for (let j = 0; j < anillos.length; j++) {
      if (i === j || !cajasSeTocan(cajas[i], cajas[j])) continue;
      for (const vertice of anillos[j]) insertarEnBorde(anillos[i], vertice);
    }
  }

  return poligonos.map((p, i) => ({
    ...p,
    coordinates: [anillos[i], ...p.coordinates.slice(1)],
  }));
}

/** Inserta `punto` en la arista de `anillo` sobre la que cae (estrictamente
 *  entre sus extremos), si no es ya uno de sus vértices. Muta el anillo. */
function insertarEnBorde(anillo: number[][], punto: number[]): void {
  if (anillo.some((v) => v[0] === punto[0] && v[1] === punto[1])) return;
  for (let k = 0; k < anillo.length - 1; k++) {
    if (distanciaASegmento(punto, anillo[k], anillo[k + 1]) < TOLERANCIA_SELLADO_GRADOS) {
      anillo.splice(k + 1, 0, [punto[0], punto[1]]);
      return;
    }
  }
}

/** Distancia de `p` al segmento `a`-`b`, o `Infinity` si su proyección cae
 *  fuera del segmento (en un extremo ya sería un vértice, no una T). */
function distanciaASegmento(p: number[], a: number[], b: number[]): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const largo2 = dx * dx + dy * dy;
  if (largo2 === 0) return Infinity;
  const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / largo2;
  if (t <= 0 || t >= 1) return Infinity;
  return Math.abs(dx * (p[1] - a[1]) - dy * (p[0] - a[0])) / Math.sqrt(largo2);
}

/** Caja envolvente ensanchada en la tolerancia del sellado. */
function cajaDe(anillo: number[][]): [number, number, number, number] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of anillo) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  const t = TOLERANCIA_SELLADO_GRADOS;
  return [minX - t, minY - t, maxX + t, maxY + t];
}

function conflictoCon(
  candidato: Candidato,
  aceptados: readonly Candidato[],
): "duplicado" | "solapado" | null {
  for (const aceptado of aceptados) {
    if (!cajasSeTocan(candidato.bbox, aceptado.bbox)) continue;
    let solape = 0;
    try {
      const interseccion = turf.intersect(
        turf.featureCollection([
          turf.polygon(candidato.item.polygon.coordinates),
          turf.polygon(aceptado.item.polygon.coordinates),
        ]),
      );
      solape = interseccion ? turf.area(interseccion) : 0;
    } catch {
      // Intersección que turf no resuelve: se trata como sin solape y la
      // última palabra la tiene la validación del servidor.
      continue;
    }
    if (solape <= SOLAPE_TOLERADO_M2) continue;
    return solape >= FRACCION_DUPLICADO * Math.max(candidato.area, aceptado.area)
      ? "duplicado"
      : "solapado";
  }
  return null;
}

function cajasSeTocan(
  a: [number, number, number, number],
  b: [number, number, number, number],
): boolean {
  return a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];
}

/** Texto para avisar qué se descartó, o `null` si no se descartó nada. */
export function fx_mensajeDepuracion(resultado: ResultadoDepuracion): string | null {
  const partes: string[] = [];
  if (resultado.invalidos > 0) {
    partes.push(`${resultado.invalidos} con errores topológicos`);
  }
  if (resultado.duplicados > 0) {
    partes.push(`${resultado.duplicados} duplicado(s)`);
  }
  if (resultado.solapados > 0) {
    partes.push(`${resultado.solapados} que se solapaban con otro`);
  }
  return partes.length > 0
    ? `Se descartaron ${partes.join(", ")}. Quedaron ${resultado.validos.length} polígono(s).`
    : null;
}

/** Nombre para una pieza del mapa: el del polígono importado que coincide
 *  con ella (mismo centroide a menos de 1 m y área a menos de 1%), o `null`.
 *  No se compara por coordenadas exactas porque el sellado de bordes puede
 *  haberle agregado vértices a la pieza. Una pieza que el usuario
 *  editó en el paso 3 ya no coincide y queda sin nombre. */
export function fx_nombreDePieza(
  pieza: Polygon,
  importados: readonly PoligonoConNombre[],
): string | null {
  try {
    const centro = turf.centroid(pieza);
    const area = turf.area(pieza);
    for (const importado of importados) {
      if (!importado.nombre) continue;
      const areaImportado = turf.area(importado.polygon);
      if (Math.abs(area - areaImportado) > 0.01 * Math.max(area, areaImportado)) continue;
      const distancia = turf.distance(centro, turf.centroid(importado.polygon), {
        units: "meters",
      });
      if (distancia < 1) return importado.nombre;
    }
  } catch {
    // Geometría ilegible: sin nombre.
  }
  return null;
}
